import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FiLock } from "react-icons/fi";
import type { Category } from "../API/Models";
import { formatExactDate, formatRelativeTime } from "../Helper/dates";
import { fetchCategories } from "../Redux/Slice/CategoriesSlice";
import type { IApplicationState } from "../Redux/Reducers";

/** Build the "Parent > Child" path label for a category by walking its parent chain. */
const buildCategoryLabel = (categories: Category[], id?: string | null) => {
	if (!id) return "";
	const map = new Map(categories.filter((node) => node.id).map((node) => [node.id as string, node]));
	const parts: string[] = [];
	const visited = new Set<string>();
	let cursor = map.get(id);
	while (cursor?.id) {
		if (visited.has(cursor.id)) break;
		visited.add(cursor.id);
		parts.push(cursor.title ?? cursor.id);
		cursor = cursor.parentId ? map.get(cursor.parentId) : undefined;
	}
	return parts.reverse().join(" > ");
};

/**
 * Read-only category list. Categories are reference data defined in code (C# manifest) and seeded by
 * the ReferenceDataReconciler on startup : there is no create/edit/delete API or UI here on purpose.
 */
const CategoriesPage: React.FC = () => {
	const dispatch = useDispatch();
	const { items: categories, loading, error, isRefreshing, lastUpdated, initialized } = useSelector(
		(state: IApplicationState) => state.categories
	);

	useEffect(() => {
		if (!initialized && !loading && !isRefreshing) {
			dispatch(fetchCategories() as any);
		}
	}, [dispatch, initialized, isRefreshing, loading]);

	return (
		<div className="min-h-full p-6">
			<div className="mx-auto w-full space-y-8">
				<div className="flex flex-wrap items-center gap-3 rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
					<div>
						<p className="text-xs uppercase tracking-widest text-slate-500">Categories</p>
						<h1 className="text-2xl font-semibold text-slate-900">Category list</h1>
						{lastUpdated && (
							<p className="text-xs text-slate-500">
								Last sync: {formatExactDate(lastUpdated)} ({formatRelativeTime(lastUpdated)})
							</p>
						)}
					</div>
					<div className="ml-auto flex flex-wrap items-center gap-3">
						<span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-medium text-slate-500">
							<FiLock className="h-3 w-3" /> Géré dans le code
						</span>
						<div className="text-sm text-slate-500">{categories.length} total</div>
						<button
							type="button"
							onClick={() => dispatch(fetchCategories(true) as any)}
							disabled={isRefreshing}
							className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 shadow-sm transition hover:border-slate-400 disabled:cursor-not-allowed disabled:opacity-60"
						>
							{isRefreshing ? "Refreshing…" : "Refresh"}
						</button>
					</div>
				</div>

				<section className="rounded-2xl border border-slate-100 bg-white shadow-sm">
					<header className="flex flex-wrap items-center justify-between gap-4 border-b border-slate-100 px-5 py-4">
						<div>
							<p className="text-sm font-medium uppercase tracking-wide text-slate-500">Categories</p>
							<p className="text-sm text-slate-500">
								Showing {categories.length} categor{categories.length === 1 ? "y" : "ies"} · reference data, read-only
							</p>
						</div>
					</header>
					{error && (
						<div className="m-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>
					)}
					<div className="overflow-x-auto">
						<table className="min-w-full divide-y divide-slate-100 text-sm">
							<thead>
								<tr className="bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-widest text-slate-500">
									<th className="px-5 py-3">Id</th>
									<th className="px-5 py-3">Key</th>
									<th className="px-5 py-3">Title</th>
									<th className="px-5 py-3">Path</th>
									<th className="px-5 py-3">Updated</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-slate-100">
								{loading ? (
									<tr>
										<td colSpan={5} className="px-5 py-6 text-center text-slate-500">
											Loading categories…
										</td>
									</tr>
								) : categories.length === 0 ? (
									<tr>
										<td colSpan={5} className="px-5 py-10 text-center text-slate-500">
											No categories found.
										</td>
									</tr>
								) : (
									categories.map((item, index) => (
										<tr key={item.id ?? item.title ?? index} className="hover:bg-slate-50">
											<td className="px-5 py-3 text-xs font-mono text-slate-600">{item.id ?? "-"}</td>
											<td className="px-5 py-3 text-xs font-mono text-slate-600">{item.key ?? "-"}</td>
											<td className="px-5 py-3 text-slate-900">{item.title ?? "-"}</td>
											<td className="px-5 py-3 text-slate-600">{buildCategoryLabel(categories, item.id)}</td>
											<td className="px-5 py-3 text-slate-500">
												<div className="text-xs font-semibold text-slate-900">
													{item.updatedAt ? formatExactDate(item.updatedAt) : "-"}
												</div>
												<div className="text-[11px] uppercase tracking-wide text-slate-400">
													{item.updatedAt ? formatRelativeTime(item.updatedAt) : ""}
												</div>
											</td>
										</tr>
									))
								)}
							</tbody>
						</table>
					</div>
				</section>
			</div>
		</div>
	);
};

export default CategoriesPage;
